import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { useAnalyticsContext } from '../analytics-context';

const formatDuration = (seconds: number) => {
  const totalSeconds = Math.round(seconds || 0);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

const metricsConfig = [
  {
    key: 'totalUsers',
    label: 'Total Users',
    formatter: (value: number) => value.toLocaleString(),
  },
  {
    key: 'newUsers',
    label: 'New Users',
    formatter: (value: number) => value.toLocaleString(),
  },
  {
    key: 'sessions',
    label: 'Sessions',
    formatter: (value: number) => value.toLocaleString(),
  },
  {
    key: 'pageViews',
    label: 'Page Views',
    formatter: (value: number) => value.toLocaleString(),
  },
  {
    key: 'averageSessionDuration',
    label: 'Avg Session Duration',
    formatter: (value: number) => formatDuration(value),
  },
  {
    key: 'bounceRate',
    label: 'Bounce Rate',
    formatter: (value: number) => `${value.toFixed(1)}%`,
  },
] as const;

const GaOverview: React.FC = () => {
  const { ga, isCustomRangePending } = useAnalyticsContext();
  const metrics = ga.overview.metrics;
  const overviewError = ga.overview.error as any;

  if (!ga.integration.isConfigured) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Google Analytics Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="py-6 text-sm text-muted-foreground">
          {ga.integration.message ||
            'Connect your Google Analytics account to view traffic metrics.'}
        </CardContent>
      </Card>
    );
  }

  if (overviewError && overviewError.status && overviewError.status !== 400) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Google Analytics Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="py-6 text-sm text-red-600">
          {overviewError.data?.message ||
            overviewError.message ||
            'Failed to load Google Analytics overview.'}
        </CardContent>
      </Card>
    );
  }

  if (ga.overview.isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metricsConfig.map(item => (
          <Card key={item.key} className="border-none shadow-sm">
            <CardContent className="py-6">
              <div className="h-8 w-32 animate-pulse rounded bg-muted/50" />
              <div className="mt-3 h-5 w-24 animate-pulse rounded bg-muted/40" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (isCustomRangePending) {
    return (
      <Card className="border-none shadow-sm">
        <CardContent className="py-6 text-sm text-muted-foreground">
          Select a start and end date to view Google Analytics insights.
        </CardContent>
      </Card>
    );
  }

  if (!metrics) {
    return (
      <Card className="border-none shadow-sm">
        <CardContent className="py-6 text-sm text-muted-foreground">
          No Google Analytics summary data available for this period.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {metricsConfig.map(item => (
        <Card key={item.key} className="border-none shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {item.label}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-foreground">
              {item.formatter(Number(metrics[item.key]))}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default GaOverview;
